import { Messages } from 'primereact/messages';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Button } from 'primereact/button';
import { useAuthHeader, useAuthUser } from '../../auth/AuthService';
import { updateUser } from '../../Services/AuthApiService';
import { Toast } from 'primereact/toast';
import { Password } from 'primereact/password';
import { useTranslation } from 'react-i18next';

export default function UserPreferenceFirm() {
    const {t, i18n} = useTranslation();
    const auth = useAuthUser();
    const authHeader = useAuthHeader();
    const msgs = useRef(null);
    const toast = useRef(null);
    const defaultValues = {
        email: auth()?.userData?.email ?? '',
        phone: auth()?.userData?.phone ?? '',
        newPassword: '',
        conNewPassword: '',
        oldPassword: ''
    };
    const {
        control,
        formState: { errors },
        handleSubmit,
        watch,
        setError,
        reset
    } = useForm({ defaultValues });

    const onSubmit = async (data) => {
        await updateUser(data, authHeader())
        .then((response) => {
            if (response.status === 200) {
                reset(defaultValues);
                toast.current.show({severity:'success', summary: 'Success', detail: response?.data, life: 3000});
            }
        })
        .catch((error) => {
            let response = error.response;
            if (response?.status === 400) {
                setError('oldPassword', {
                    type: "server",
                    message: response?.data,
                });
                toast.current.show({severity:'error', summary: 'Error', detail: response?.data, life: 3000});
            }
        });
    };

    const getFormErrorMessage = (name) => {
        return errors[name] ? <small className="p-error pt-1 pb-3 text-left">{errors[name].message}</small> : <small className="p-error">&nbsp;</small>;
    };

    useEffect(() => {
        msgs.current.show(
            { sticky: true, severity: 'info', summary: <span className='font-bold text-lg'>{t('Information')}!&nbsp;&nbsp;</span>, detail: t('To update your information or change your password, current password is required.'), closable: false }
        );
    }, []); 

    return (
        <>
            <div className='relative h-full'>
                <div className="flex justify-content-center pt-7">
                    <div className='flex w-11 lg:w-9 justify-content-center'>
                        <div className="grid w-full">
                            <div className='col-12'>
                                <Messages ref={msgs} />  
                                <div className="card p-4 shadow-6 border-round-xl bg-card"> 
                                    <div className='grid align-items-center'>
                                        <div className="col-12 text-xl font-bold border-bottom-1 mb-3">{t('Preference')}</div>
                                        <form onSubmit={handleSubmit(onSubmit)} className='w-full p-2'>
                                            <div className='flex flex-column w-4'>
                                                <Controller
                                                    name="email"
                                                    control={control}
                                                    render={({ field, fieldState }) => (
                                                        <>
                                                            <label htmlFor="email">{t('Email')}</label>
                                                            <InputText id="email" value={field.value} className={classNames({ 'p-invalid': fieldState.error })+" w-full bg-login-card"} onChange={(e) => field.onChange(e.target.value)}/>
                                                            {getFormErrorMessage(field.name)}
                                                        </>
                                                    )}
                                                />
                                                <Controller
                                                    name="phone"
                                                    control={control}
                                                    rules={{ required: 'Phone is required.' }}
                                                    render={({ field, fieldState }) => (
                                                        <>
                                                            <label htmlFor="phone">{t('Phone')}</label>
                                                            <InputText id="phone" value={field.value} className={classNames({ 'p-invalid': fieldState.error })+" w-full bg-login-card"} onChange={(e) => field.onChange(e.target.value)}/>
                                                            {getFormErrorMessage(field.name)}
                                                        </>
                                                    )}
                                                />
                                                <Controller
                                                    name="oldPassword"
                                                    control={control}
                                                    rules={{ 
                                                        required: 'Password is required.'
                                                    }}
                                                    render={({ field, fieldState }) => (
                                                        <>
                                                            <label htmlFor="password">{t('Current Password')}</label>
                                                            <Password id="password" value={field.value} inputClassName={classNames({ 'p-invalid': fieldState.error })+" w-full bg-login-card"} onChange={(e) => field.onChange(e.target.value)}/>
                                                            {getFormErrorMessage(field.name)}
                                                        </>
                                                    )}
                                                />
                                                <Controller
                                                    name="newPassword"
                                                    control={control}
                                                    rules={{ 
                                                        required: 'New password is required.',
                                                        validate: (val) => {
                                                            if (val.length < 8) {
                                                                return "Minimum 8 characters long";
                                                            }
                                                        }
                                                    }}
                                                    render={({ field, fieldState }) => (
                                                        <>
                                                            <label htmlFor="newPassword">{t('New Password')}</label>
                                                            <Password id="newPassword" value={field.value} inputClassName={classNames({ 'p-invalid': fieldState.error })+" w-full bg-login-card"} onChange={(e) => field.onChange(e.target.value)}/>
                                                            {getFormErrorMessage(field.name)}
                                                        </>
                                                    )}
                                                />
                                                <Controller
                                                    name="conNewPassword"
                                                    control={control}
                                                    rules={{
                                                        required: 'Confirm new password is required.',
                                                        validate: (val) => {
                                                            if (watch('newPassword') != val) {
                                                                return "Confirm new password do not match";
                                                            }
                                                        }
                                                    }}
                                                    render={({ field, fieldState }) => (
                                                        <>
                                                            <label htmlFor="conNewPassword">{t('Confirm New Password')}</label>
                                                            <Password id="conNewPassword" value={field.value} inputClassName={classNames({ 'p-invalid': fieldState.error })+" w-full bg-login-card"} onChange={(e) => field.onChange(e.target.value)}/>
                                                            {getFormErrorMessage(field.name)}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                            <Button type="submit" label={t('Submit')} className="w-2 p-3 text-xl" ></Button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Toast ref={toast} />
        </>
    )
}